/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import { useAgencyClients } from '@/features/visits/hooks/useAgencyClients';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useCreateSaleForm } from './useCreateSaleForm';
import { CreateSaleFields } from './CreateSaleFields';
import { CreateSaleSubmitButton } from './SubmitButton';

export interface CreateSaleDrawerProps {
  agencyId: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Create drawer for a sale entered directly, with no deal behind it.
 * Uses AdaptiveFormShell + react-hook-form + zod, matching the pattern of
 * VisitDrawer, ClientDrawer, PropertyDrawer and CreateLaunchDrawer.
 */
export function CreateSaleDrawer({ agencyId, open, onClose, onCreated }: CreateSaleDrawerProps) {
  const { t } = useTranslation('features/sales');
  const agencyMembers = useAgency((state) => state.agency?.members);
  const { properties } = useAgencyProperties(open ? agencyId : undefined);
  const { clients } = useAgencyClients(open ? agencyId : undefined);

  const { form, isSubmitting, hasValidationErrors, handleSubmit } = useCreateSaleForm({
    agencyId,
    clients,
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const propertyOptions: SearchAutocompleteOption[] = useMemo(
    () => properties.map((p) => ({ id: p.id, label: p.label, sublabel: p.sublabel })),
    [properties],
  );

  const clientOptions: SearchAutocompleteOption[] = useMemo(
    () => clients.map((c) => ({ id: c.id, label: c.label, sublabel: c.sublabel })),
    [clients],
  );

  const brokerOptions: SearchAutocompleteOption[] = useMemo(
    () => (agencyMembers ?? []).map((m) => ({
      id: m.email,
      label: `${m.first_name} ${m.last_name}`.trim(),
      sublabel: m.email,
    })),
    [agencyMembers],
  );

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('create.title')}
      density="compact"
      footer={<CreateSaleSubmitButton isSubmitting={isSubmitting} onSubmit={handleSubmit} />}
    >
      <CreateSaleFields
        form={form}
        propertyOptions={propertyOptions}
        clientOptions={clientOptions}
        brokerOptions={brokerOptions}
        hasValidationErrors={hasValidationErrors}
      />
    </AdaptiveFormShell>
  );
}
